import { RcAPI, getApiBaseUrl } from '../../components/common/rcApi';
import { strings } from '../../localization.js';

export const ACTION_TYPES = {

    SET_ALL_ASSETS: "SET_ALL_ASSETS", // used when loading/refresh all settings
    RESET_ALL: "RESET_ALL", // used when signing out

    SET_ASSET: "SET_ASSET",
    REMOVE_ASSET: "REMOVE_ASSET",

    //RESET_ASSET: "RESET_ASSET",

    SET_ALL_BEACONS: "SET_ALL_BEACONS", // used when loading/refresh all settings

    SET_BEACON: "SET_BEACON",
    REMOVE_BEACON: "REMOVE_BEACON",

}


const NEW_ASSET_TEMPORARY_UUID = undefined; // It is literally undefined. It is used to indicate that the asset is new and has not been assigned a UUID from the cloud
const NEW_BEACON_TEMPORARY_UUID = undefined; // It is literally undefined. It is used to indicate that the beacon is new and has not been assigned a UUID from the cloud

const KNOWN_SENSOR_TYPES = {
    ASSET_TRACKER_STATUS_BATTERY_PERCENT: "ASSET_TRACKER_STATUS_BATTERY_PERCENT", // {ts: 1717082137, type: 2, value: 98, text: null, name: "ASSET_TRACKER_STATUS_BATTERY_PERCENT"}
    ASSET_TRACKER_STATUS_RSSI: "ASSET_TRACKER_STATUS_RSSI",
};

export const BEACON_ASSIGNMENT_STATUS = {
    ALL: "ALL",
    ASSIGNED: "ASSIGNED",
    UNASSIGNED: "UNASSIGNED",
};

export const BEACON_VISIBILITY_STATUS = {
  ALL: 'all',
  VISIBLE: 'visible',
  HIDDEN: 'hidden',
};

const NEW_ASSET_TEMPLATE = { // reflects the same structure as the asset data from the API, except beaconUuid and batteryPercentage which are local only properties. Also, renaming some properties from snake_case to camelCase
    editing: false,          // this asset data is being edited
    pending: false,         // this asset data is being loaded or is being changed or refreshed
    errorMessage: undefined,// an error occurred that affects this asset (i.e., this asset could not be loaded)

    visible: true,          // new assets could theoretically be visible by default and show up on the map once the beacon is assigned.

    assetUuid: NEW_ASSET_TEMPORARY_UUID,   // undefined until the asset is POSTed to the cloud, "asset_uuid" from cloud
    serialNo: undefined,    // "serial_no" from cloud
    name: strings.asset_new_default_name,        // "Generator",
    nameChangePending: false,// legacy support for editing name independently, indicates that the name is being changed
    description: undefined, // "Big Genny",
    make: undefined,        // "Champion",
    model: undefined,       // "abcd-1234",

    // details of the last raven that has seen the asset/beacon
    carPersona: undefined,  
    enclosureSerialNo: undefined,

    // if beacon, then geo-location data will be available
    ts: undefined,          // 1713797266,
    latitude: undefined,    // 45.414364,-75.69796,
    longitude: undefined,   // -75.69796,

    compass: undefined,     // undefined, (null from cloud, at time of writing)
    speed: undefined,       // undefined, (null from cloud, at time of writing)
    altitude: undefined,    // undefined, (null from cloud, at time of writing)
    accuracy: undefined,    // undefined, (null from cloud, at time of writing)
    batteryPercentage: undefined, // a convenience property derived from sensors.ASSET_TRACKER_STATUS_BATTERY_PERCENT 
    beaconUuid: undefined,   // only the beacon id (beacons metadata are stored in beacons array)

    originalAsset: undefined // used to store the original asset data when editing begins, so changes can be cancelled
};

const NEW_BEACON_TEMPLATE = { // reflects the same structure as the beacon data from the API, except for assetUuid which is local only
    editing: false,          // this asset data is being edited
    pending: false,         // this asset data is being loaded or is being changed or refreshed
    errorMessage: undefined,// an error occurred that affects this asset (i.e., this asset could not be loaded)

    visibility: true,

    // details of the last raven that has seen the asset/beacon
    carPersona: undefined,  
    enclosureSerialNo: undefined,

    beaconUuid: NEW_BEACON_TEMPORARY_UUID,  // "beacon_uuid" from cloud, "c1444240-bf91-4053-997f-46f2ee92e8a7",
    serialNo: undefined, // "serial_no" from cloud, "78d9e91e8956",
    make: undefined,        // "Momentum",
    model: undefined,       // "Toolie",
    btMac: undefined,       // "bt_mac" from cloud, "78d9e91e8956",

    sensors: {},            // "sensors" from cloud, { "ASSET_TRACKER_STATUS_BATTERY_PERCENT":},

    assetUuid: undefined,     // asset data is stored in assets array
};

const INITIAL_STATE = { // used during initialization and RESET_ALL

    assetsLoaded: false,
    assetsPending: true, // a request that could change any asset data is in progress (i.e., show a spinner)
    assetsErrorMessage: undefined, // an error occurred that affects all assets (i.e., assets could not be loaded))

    beaconsLoaded: false,
    beaconsPending: true,// a request that could change any beacon data is in progress (i.e., show a spinner)
    beaconsErrorMessage: undefined,// an error occurred that affects all beacons (i.e., beacons could not be loaded))

    assets: [/*{ see NEW_ASSET_TEMPLATE for syntax/structure }*/],

    beacons: [/* see NEW_BEACON_TEMPLATE for syntax/structure}*/],

    // ideas for how this can be extended:
    //trackers: [],             // not used yet
    //monitors: []              // not used yet
};

// ACTION_CREATORS deviates from Modular Redux (Ducks) Rule #2: MUST export its action creators as functions (https://github.com/erikras/ducks-modular-redux)
// Here, we are using an exported object, ACTION_CREATORS, to group all action creators together similarly to ACTION_TYPES.
// To the best of my knowledge, this is not a requirement for Ducks, but it seems like a good practice to keep all action creators together without needing something like a naming convention
export const ACTION_CREATORS = { // state update "actions" (i.e. each action takes new data to build a payload, which is used by a reducer as a "delta" to update the state)

    // updatingAssets sets the state to indicate that assets data is being loaded or is being changed or refreshed
    updatingAssets: () => {
        return {
            type: ACTION_TYPES.SET_ALL_ASSETS, // what type of reducer action to take
            payload: { // payload delta to apply to state in the reducer
                assetsPending: true, // this means "updating" is in progress
                assetsErrorMessage: undefined // error message is reset while changes are pending (i.e., any previous error was from a previous attempt)
            }
        }
    },

    // setAssets sets the state to indicate that an attempt to load or change the data in the cloud was completed either successfully or with an error
    setAssets: (assets = undefined, errorMessage = undefined, updatingProps = false) => {

        return {
            type: ACTION_TYPES.SET_ALL_ASSETS,
            payload: {
                assetsPending: false, // this means "updating" is not in progress, i.e. the data is set
                assets: assets,
                assetsErrorMessage: errorMessage,
                updatingProps: updatingProps
            }
        }
    },

    updatingAsset: (asset) => {
        return {
            type: ACTION_TYPES.SET_ASSET,
            payload: {
                asset: {
                    ...asset,
                    pending: true,
                    errorMessage: undefined
                }
            }
        }
    },

    updatingAssetName: (asset) => {
        return {
            type: ACTION_TYPES.SET_ASSET,
            payload: {
                asset: {
                    ...asset,
                    nameChangePending: true,
                    errorMessage: undefined
                }
            }
        }
    },

    setAsset: (asset, errorMessage = undefined, replaceNewLocalAsset = false) => {
        return {
            type: ACTION_TYPES.SET_ASSET,
            payload: {
                asset: {
                    ...asset,
                    pending: false,
                    nameChangePending: false,
                    errorMessage: errorMessage,
                },
                replaceNewLocalAsset: replaceNewLocalAsset
            }
        }
    },

    removeAsset: (assetUuid) => {

        return {
            type: ACTION_TYPES.REMOVE_ASSET,
            payload: {
                asset: {
                    assetUuid: assetUuid
                }
            }
        }
    },

    updatingBeacons: () => {
        return {
            type: ACTION_TYPES.SET_ALL_BEACONS,
            payload: {
                beaconsPending: true,
                beaconsErrorMessage: undefined
            }
        }
    },

    setBeacons: (beacons = undefined, errorMessage = undefined) => {
            
        return {
            type: ACTION_TYPES.SET_ALL_BEACONS,
            payload: {
                beaconsPending: false,
                beacons: beacons,
                beaconsErrorMessage: errorMessage
            }
        }
    },

    updatingBeacon: (beacon) => {
        return {
            type: ACTION_TYPES.SET_BEACON,
            payload: {
                beacon: {
                    ...beacon,
                    pending: true,
                    errorMessage: undefined
                }
            }
        }
    },

    setBeacon: (beacon, errorMessage = undefined, replaceNewLocalBeacon = false) => {
        return {
            type: ACTION_TYPES.SET_BEACON,
            payload: {
                beacon: {
                    ...beacon,
                    pending: false,
                    errorMessage: errorMessage,
                    replaceNewLocalBeacon: replaceNewLocalBeacon
                }
            }
        }
    },

    removeBeacon: (beaconUuid) => {
        return {
            type: ACTION_TYPES.REMOVE_BEACON,
            payload: {
                beacon: {
                    beaconUuid: beaconUuid
                }
            }
        }
    },

    // resetAll sets the local data state back to INITIAL_STATE
    // useful during stuff like signing out
    resetAll: () => {
        return {
            type: ACTION_TYPES.RESET_ALL
        }
    },
};

export const clearAssetBeaconAssignment = (asset, beacon) => {
    if (asset) {
        asset.beaconUuid = undefined;
        asset.latitude = undefined;
        asset.longitude = undefined;
        asset.ts = undefined;
        asset.compass = undefined;
        asset.speed = undefined;
        asset.altitude = undefined;
        asset.accuracy = undefined;
        asset.batteryPercentage = undefined;
        asset.carPersona = undefined;
        asset.enclosureSerialNo = undefined;
    }

    // clear asset properties for beacon
    if (beacon) {
        beacon.assetUuid = undefined;
        beacon.assetSerialNo = undefined;
        beacon.assetName = undefined;
        beacon.assetDescription = undefined;
        beacon.assetMake = undefined;
        beacon.assetModel = undefined;
    }
    
    return asset;
};

export const setAssetBeaconAssignment = (asset, beacon) => {
    asset.beaconUuid = beacon.beaconUuid;
    asset.latitude = beacon.latitude;
    asset.longitude = beacon.longitude;
    asset.ts = beacon.ts;
    asset.compass = beacon.compass;
    asset.speed = beacon.speed;
    asset.altitude = beacon.altitude;
    asset.accuracy = beacon.accuracy;
    asset.batteryPercentage = beacon.sensors && beacon.sensors[KNOWN_SENSOR_TYPES.ASSET_TRACKER_STATUS_BATTERY_PERCENT] ? beacon.sensors[KNOWN_SENSOR_TYPES.ASSET_TRACKER_STATUS_BATTERY_PERCENT].value : undefined;
    asset.carPersona = beacon.carPersona;
    asset.enclosureSerialNo = beacon.enclosureSerialNo;

    // set asset properties for beacon
    beacon.assetUuid = asset.assetUuid;
    // beacon.assetSerialNo = asset.serialNo; *** Dont need to set these props because they are not used so far
    // beacon.assetName = asset.name;
    // beacon.assetDescription = asset.description;
    // beacon.assetMake = asset.make;
    // beacon.assetModel = asset.model;    
    
    return asset;
};

export const getLatestTimestamp = (asset) => {
        if(!asset.sensors) return asset.ts;
        let latestTimestamp = 0;
    
        Object.keys(asset.sensors).forEach(sensorType => {
            if (asset.sensors[sensorType].ts && asset.sensors[sensorType].ts > latestTimestamp) {
                latestTimestamp = asset.sensors[sensorType].ts;
            }
        });
        const t = latestTimestamp > 0 ? latestTimestamp : asset.ts;

        return t;    
}

// takes an asset from the cloud or from local state and ensures that it has all the properties to be saved in local state.
const convertToLocalAsset = (asset) => {

    return {

        editing: asset.hasOwnProperty("editing") ? asset.editing : NEW_ASSET_TEMPLATE.editing,
        pending: asset.hasOwnProperty("pending") ? asset.pending : NEW_ASSET_TEMPLATE.pending,
        errorMessage: asset.errorMessage ? asset.errorMessage : NEW_ASSET_TEMPLATE.errorMessage,

        visible: asset.hasOwnProperty("visible") ? asset.visible : NEW_ASSET_TEMPLATE.visible,

        assetUuid: asset.hasOwnProperty("assetUuid") ? asset.assetUuid : asset.asset_uuid ? asset.asset_uuid : undefined,

        serialNo: asset.serialNo ? asset.serialNo : asset.serial_no ? asset.serial_no : undefined,
        name: asset.name,
        nameChangePending: asset.hasOwnProperty("nameChangePending") ? asset.nameChangePending : NEW_ASSET_TEMPLATE.nameChangePending,
        description: asset.description,
        make: asset.make,
        model: asset.model,

        carPersona: asset.carPersona ? asset.carPersona : asset.car_persona ? asset.car_persona : NEW_ASSET_TEMPLATE.carPersona,
        enclosureSerialNo: asset.enclosureSerialNo ? asset.enclosureSerialNo : asset.enclosure_serial_no ? asset.enclosure_serial_no : NEW_ASSET_TEMPLATE.enclosureSerialNo,

        // if assigned to a beacon and beacon has geo-location data
        latitude: asset.latitude, 
        longitude: asset.longitude,
        compass: asset.compass,
        speed: asset.speed,
        altitude: asset.altitude,
        accuracy: asset.accuracy,
        
        //sensors: asset.sensors ? asset.sensors : {}, // if storing sensor data in assets array, uncomment this line
        ts: getLatestTimestamp(asset),
        batteryPercentage: asset.sensors && asset.sensors[KNOWN_SENSOR_TYPES.ASSET_TRACKER_STATUS_BATTERY_PERCENT] ? asset.sensors[KNOWN_SENSOR_TYPES.ASSET_TRACKER_STATUS_BATTERY_PERCENT].value : asset.batteryPercentage,

        beaconUuid: asset.hasOwnProperty("beaconUuid") ? asset.beaconUuid : asset.beacon ? asset.beacon : undefined,

        originalAsset: asset.originalAsset ? asset.originalAsset : NEW_ASSET_TEMPLATE.originalAsset
    };
};

const convertToLocalBeacon = (beacon) => {
    return {

        editing: beacon.hasOwnProperty("editing") ? beacon.editing : NEW_BEACON_TEMPLATE.editing,
        pending: beacon.hasOwnProperty("pending") ? beacon.pending : NEW_BEACON_TEMPLATE.pending,
        errorMessage: beacon.errorMessage ? beacon.errorMessage : NEW_BEACON_TEMPLATE.errorMessage,

        beaconUuid: beacon.beaconUuid ? beacon.beaconUuid : beacon.beacon_uuid,
        serialNo: beacon.serialNo ? beacon.serialNo : beacon.serial_no,

        make: beacon.make,
        model: beacon.model,
        btMac: beacon.btMac ? beacon.btMac : beacon.bt_mac,

        visibility: beacon.hasOwnProperty("visibility") ? beacon.visibility : NEW_BEACON_TEMPLATE.visibility,

        ts: getLatestTimestamp(beacon), // timestamp of the latest location update
        latitude: beacon.latitude,
        longitude: beacon.longitude,
        compass: beacon.compass,
        speed: beacon.speed,
        altitude: beacon.altitude,
        accuracy: beacon.accuracy,

        
        carPersona: beacon.carPersona ? beacon.carPersona : beacon.car_persona ? beacon.car_persona : NEW_BEACON_TEMPLATE.carPersona,
        enclosureSerialNo: beacon.enclosureSerialNo ? beacon.enclosureSerialNo : beacon.enclosure_serial_no ? beacon.enclosure_serial_no : NEW_BEACON_TEMPLATE.enclosureSerialNo,

        sensors: beacon.sensors ? beacon.sensors : {},

        // if assigned to an asset
        assetSerialNo: beacon.assetSerialNo ? beacon.assetSerialNo : beacon.asset_serial_no,
        assetName: beacon.assetName ? beacon.assetName : beacon.asset_name,
        assetDescription: beacon.assetDescription ? beacon.assetDescription : beacon.asset_description,
        assetMake: beacon.assetMake ? beacon.assetMake : beacon.asset_make,
        assetModel: beacon.assetModel ? beacon.assetModel : beacon.asset_model,

        assetUuid: beacon.assetUuid ? beacon.assetUuid : beacon.asset_uuid ? beacon.asset_uuid : undefined
    };
};

const convertToRemoteAssetUpdate = (asset) => {
    return {
        //assetUuid: asset.assetUuid,
        serialNo: asset.serialNo,
        name: asset.name,
        description: asset.description,
        make: asset.make,
        model: asset.model,
        //beaconUuid: asset.beaconUuid
    };
};

const convertToRemoteBeaconUpdate = (beacon) => {
    return {
        visibility: beacon.visibility,
    };
};

// Modular Redux (Ducks) Rule #1: MUST export default a function called reducer() (https://github.com/erikras/ducks-modular-redux)
export default function assetMonitoringReducer (state = INITIAL_STATE, action) {

    // initiate scoped variables for the switch statement
    // this avoids declaring similar variables with unique names for each case
    let assetsFromState;
    let assetFromState;
    let beaconsFromState;

    const beaconsMergedMap = {}; // used to prevent duplicate beacons and produce a merged list of unique beacons
    const assetsMergedMap = {}; // used to prevent duplicate assets and produce a merged list of unique assets

    let assetUuid;
    let beaconUuid;
    let mergedBeacon;

    // TODO: as the list a shared variables grows, consider making helper functions for merging and cleaning data for similar ops below

    switch (action.type) {

        case ACTION_TYPES.SET_ALL_ASSETS:

            if (!action.payload.assets) { // check if this is a basic status update (i.e. pending to true) and no new asset data is being set
                return { // return a new state object which combines the existing state with the payload "delta" for the action type
                    ...state, // carry over everything else in state
                    assetsPending: action.payload.assetsPending,
                    assetsErrorMessage: action.payload.assetsErrorMessage
                };
            }

            
            if (action.payload.assets && action.payload.updatingProps) { // check if this is an assets' prop update
                return { // return a new state object which combines the existing state with the payload "delta" for the action type
                    ...state, // carry over everything else in state
                    assets: action.payload.assets,
                    assetsPending: action.payload.assetsPending,
                    assetsErrorMessage: action.payload.assetsErrorMessage
                };
            }

            // New asset data is being set.
            // Assumption: an all assets data update is for assets loaded from the cloud.
            // Therefore, merge thd asset data with the existing state to preserve any local properties and camelCase keys

            assetsFromState = state.assets ? state.assets : []; // assets are undefined on initial load
            beaconsFromState = state.beacons ? state.beacons : []; // beacons are undefined on initial load

            // Use beaconsFromState to preserve what may be a more complete list of all beacons
            // Also, the beacons themselves may have additional data that was loaded individually
            beaconsFromState.forEach(beacon => {
                beaconsMergedMap[beacon.beaconUuid] = beacon; // no need to clean the data because these are beacons from the state (already cleaned)
            });
    
            // Use assetsFromState to preserve local values such as 'visible' on the map that were set during this session
            assetsFromState.forEach(asset => {
                assetsMergedMap[asset.assetUuid] = asset; // no need to clean the data because these are assets from the state (already cleaned)
            });

            action.payload.assets.forEach(asset => {

                beaconUuid = undefined;

                if (asset.beacon) {

                    beaconUuid = asset.beacon.beacon_uuid; // beacons from the cloud response use snake_case

                    mergedBeacon = convertToLocalBeacon(asset.beacon);

                    // add the assetUuid assignment to the beacon
                    mergedBeacon.assetUuid = asset.asset_uuid; // store just the UUID

                    const beaconFromState = beaconsMergedMap[beaconUuid];

                    // initiate or carry-over the local state properties
                    if (!beaconFromState) {
                        mergedBeacon.editing = NEW_BEACON_TEMPLATE.editing;
                        mergedBeacon.pending = NEW_BEACON_TEMPLATE.pending;
                        mergedBeacon.errorMessage = NEW_BEACON_TEMPLATE.errorMessage;
                    } 
                    else {
                        mergedBeacon.editing = beaconFromState.editing;
                        mergedBeacon.pending = beaconFromState.pending;
                        mergedBeacon.errorMessage = beaconFromState.errorMessage;
                        mergedBeacon.latitude = beaconFromState.latitude;
                        mergedBeacon.longitude = beaconFromState.longitude;
                        mergedBeacon.ts = beaconFromState.ts;
                        mergedBeacon.compass = beaconFromState.compass;
                        mergedBeacon.speed = beaconFromState.speed;
                        mergedBeacon.altitude = beaconFromState.altitude;
                        mergedBeacon.accuracy = beaconFromState.accuracy;
                        mergedBeacon.sensors = beaconFromState.sensors;
                    }

                    beaconsMergedMap[beaconUuid] = mergedBeacon;
                }

                const mergedAsset = convertToLocalAsset(asset);

                // add the beaconUuid assignment to the asset
                mergedAsset.beaconUuid = beaconUuid; // store just the UUIDs

                // initiate or carry-over the local state properties
                if (!assetsMergedMap[asset.asset_uuid]) {
                    mergedAsset.editing = NEW_ASSET_TEMPLATE.editing;
                    mergedAsset.pending = NEW_ASSET_TEMPLATE.pending;
                    mergedAsset.errorMessage = NEW_ASSET_TEMPLATE.errorMessage;
                    mergedAsset.visible = NEW_ASSET_TEMPLATE.visible;
                    mergedAsset.nameChangePending = NEW_ASSET_TEMPLATE.nameChangePending;
                } else {
                    mergedAsset.editing = assetsMergedMap[asset.asset_uuid].editing;
                    mergedAsset.pending = assetsMergedMap[asset.asset_uuid].pending;
                    mergedAsset.errorMessage = assetsMergedMap[asset.asset_uuid].errorMessage;
                    mergedAsset.visible = assetsMergedMap[asset.asset_uuid].visible;
                    mergedAsset.nameChangePending = assetsMergedMap[asset.asset_uuid].nameChangePending;
                }

                assetsMergedMap[asset.asset_uuid] = mergedAsset;
            });

            // update the action payload with the cleaned data
            action.payload.assets = Object.values(assetsMergedMap); // convert the map to an array of values
            action.payload.beacons = Object.values(beaconsMergedMap);

            return { // return a new state object which combines the existing state with the payload "delta" for the action type
                ...state, // carry over everything else in state
                assetsLoaded: true, // mark as loaded since we're setting assets from the cloud
                ...action.payload // update the specific properties which were targetted and updated in the action/action-creator payload
            };
        
        case ACTION_TYPES.RESET_ALL:

            return {
                ...INITIAL_STATE
            };

        case ACTION_TYPES.SET_ASSET:


            if (!action.payload.asset) {
                console.error("Data assetMonitoring reducer SET_ASSET: asset is required");
                return {
                    ...state
                };
            }

            assetUuid = action.payload.asset.assetUuid ? action.payload.asset.assetUuid : action.payload.asset.asset_uuid;

            assetsFromState = state.assets ? state.assets : [];

            if (action.payload.replaceNewLocalAsset) { // this flag is set on a successful response to an add-asset request (it will have the new asset's UUID from the cloud)

                assetsFromState = assetsFromState.map(asset => {
                    if (asset.assetUuid === NEW_ASSET_TEMPORARY_UUID) {
                        asset.assetUuid = assetUuid; // assign the new asset_uuid from the cloud after a successful add-asset request
                    }
                    return asset;
                });
            }

            assetFromState = assetsFromState.find(asset => asset.assetUuid === assetUuid);

            if (!assetFromState) {

                // add new asset
                assetsFromState.push(convertToLocalAsset(action.payload.asset));

                return {
                    ...state,
                    assets: [...assetsFromState]
                }

            } else {
                // update existing asset
                return {
                    ...state,
                    assets: assetsFromState.map(asset => { // update asset in the list
                        if (asset.assetUuid === assetUuid) {
                            return convertToLocalAsset(action.payload.asset);
                        }
                        return asset;
                    })
                };    
            }

        case ACTION_TYPES.REMOVE_ASSET:

            if (!action.payload.asset) {
                console.error("Data assetMonitoring reducer REMOVE_ASSET: asset is required");
                return {
                    ...state
                };
            }

            assetUuid = action.payload.asset.assetUuid ? action.payload.asset.assetUuid : action.payload.asset.asset_uuid;

            assetsFromState = state.assets ? state.assets : [];

            return {
                ...state,
                assets: assetsFromState.filter(asset => asset.assetUuid !== assetUuid)
            }

        case ACTION_TYPES.SET_ALL_BEACONS:

            if (!action.payload.beacons) {
                return {
                    ...state,
                    beaconsPending: action.payload.beaconsPending,
                    beaconsErrorMessage: action.payload.beaconsErrorMessage
                };
            }

            // New beacons data is being set.
            // Assumption: a beacons data update is for beacons loaded from the cloud.
            // Therefore, merge the beacon data with the existing state to preserve any local properties and camelCase keys

            beaconsFromState = state.beacons ? state.beacons : []; // beacons are undefined on initial load
            assetsFromState = state.assets ? state.assets : []; // assets are undefined on initial load

            beaconsFromState.forEach(beacon => {
                beaconsMergedMap[beacon.beaconUuid] = beacon; // no need to clean the data because these are beacons from the state (already cleaned)
            });

            assetsFromState.forEach(asset => {
                assetsMergedMap[asset.assetUuid] = asset; // no need to clean the data because these are assets from the state (already cleaned)
            });

            action.payload.beacons.forEach(beacon => {

                beaconUuid = beacon.beacon_uuid; // beacons from the cloud response use snake_case

                if (beacon.asset_uuid) {
                    /*
                        To do: Check again and delete if not needed

                        Explanation:
                        No Need to merge asset data here because it is already merged in the SET_ALL_ASSETS action
                        And that includes the details of beacon assigned to the asset
                        In fact, merging the asset data here is undoing some of the work done in SET_ALL_ASSETS
                        Such as latitude and longitude and battery life
                    */

                    // const mergedAsset = convertToLocalAsset({
                    //     asset_uuid: beacon.asset_uuid,
                    //     serial_no: beacon.asset_serial_no,
                    //     name: beacon.asset_name,
                    //     description: beacon.asset_description,
                    //     make: beacon.asset_make,
                    //     model: beacon.asset_model,

                    //     sensors: beacon.sensors, // if storing sensor data in assets array, uncomment this line

                    //     beaconUuid: beaconUuid
                    // });

                    // if (!assetsMergedMap[beacon.asset_uuid]) {
                    //     mergedAsset.beaconUuid = beaconUuid;
                    //     mergedAsset.editing = NEW_ASSET_TEMPLATE.editing;
                    //     mergedAsset.pending = NEW_ASSET_TEMPLATE.pending;
                    //     mergedAsset.errorMessage = NEW_ASSET_TEMPLATE.errorMessage;
                    //     mergedAsset.visible = NEW_ASSET_TEMPLATE.visible;
                    //     mergedAsset.nameChangePending = NEW_ASSET_TEMPLATE.nameChangePending;    
                    // } else {
                    //     assetsMergedMap[beacon.asset_uuid].beaconUuid = beaconUuid;
                    //     mergedAsset.editing = assetsMergedMap[beacon.asset_uuid].editing;
                    //     mergedAsset.pending = assetsMergedMap[beacon.asset_uuid].pending;
                    //     mergedAsset.errorMessage = assetsMergedMap[beacon.asset_uuid].errorMessage;
                    //     mergedAsset.visible = assetsMergedMap[beacon.asset_uuid].visible;
                    //     mergedAsset.nameChangePending = assetsMergedMap[beacon.asset_uuid].nameChangePending;    
                    // }

                    // assetsMergedMap[beacon.asset_uuid] = mergedAsset;

                }
                mergedBeacon = convertToLocalBeacon(beacon);

                // initiate or carry-over the local state properties
                if (!beaconsMergedMap[beaconUuid]) {
                    mergedBeacon.editing = NEW_BEACON_TEMPLATE.editing;
                    mergedBeacon.pending = NEW_BEACON_TEMPLATE.pending;
                    mergedBeacon.errorMessage = NEW_BEACON_TEMPLATE.errorMessage;
                } else {
                    mergedBeacon.editing = beaconsMergedMap[beaconUuid].editing;
                    mergedBeacon.pending = beaconsMergedMap[beaconUuid].pending;
                    mergedBeacon.errorMessage = beaconsMergedMap[beaconUuid].errorMessage;
                }

                beaconsMergedMap[beaconUuid] = mergedBeacon;
            });

            // update the action payload with the cleaned data
            action.payload.beacons = Object.values(beaconsMergedMap); // convert the map to an array of values
            action.payload.assets = Object.values(assetsMergedMap); // convert the map to an array of values

            return { // return a new state object which combines the existing state with the payload "delta" for the action type
                ...state, // carry over everything else in state
                beaconsLoaded: true, // mark as loaded since we're setting beacons from the cloud
                ...action.payload // update the specific properties which were targetted and updated in the action/action-creator payload
            };

        case ACTION_TYPES.SET_BEACON:

            if (!action.payload.beacon) {
                console.error("Data assetMonitoring reducer SET_BEACON: beacon is required");
                return {
                    ...state
                };
            }

            beaconUuid = action.payload.beacon.beaconUuid ? action.payload.beacon.beaconUuid : action.payload.beacon.beacon_uuid;

            beaconsFromState = state.beacons ? state.beacons : [];

            if (action.payload.replaceNewLocalBeacon) { // this flag is set on a successful response to an add-beacon request (it will have the new beacon's UUID from the cloud)
                beaconsFromState = beaconsFromState.map(beacon => {
                    if (beacon.beaconUuid === NEW_BEACON_TEMPORARY_UUID) {
                        beacon.beaconUuid = beaconUuid; // assign the new beaconUuid from the cloud after a successful add-beacon request
                    }
                    return beacon;
                });
            }

            const beaconFromState = beaconsFromState.find(beacon => beacon.beaconUuid === beaconUuid);

            if (!beaconFromState) {

                // add new beacon
                beaconsFromState.push(convertToLocalBeacon(action.payload.beacon));

                return {
                    ...state,
                    beacons: [...beaconsFromState]
                }
            } else {
                    
                // update existing beacon
                return {
                    ...state,
                    beacons: beaconsFromState.map(beacon => { // update beacon in the list
                        if (beacon.beaconUuid === beaconUuid) {
                            return convertToLocalBeacon(action.payload.beacon);
                        }
                        return beacon;
                    })
                };
            }

        case ACTION_TYPES.REMOVE_BEACON:

            if (!action.payload.beacon) {
                console.error("Data assetMonitoring reducer REMOVE_BEACON: beacon is required");
                return {
                    ...state
                };
            }

            beaconUuid = action.payload.beacon.beaconUuid ? action.payload.beacon.beaconUuid : action.payload.beacon.beacon_uuid;

            beaconsFromState = state.beacons ? state.beacons : [];

            return {
                ...state,
                beacons: beaconsFromState.filter(beacon => beacon.beaconUuid !== beaconUuid)
            };

        default:
            return state;
    }
};


export class AssetMonitoringApi {

    static getAccountAssets() {

        const apiBaseUrl = getApiBaseUrl('/user-v1');

        if (!apiBaseUrl) {
            console.error("Data assetMonitoring getAccountAssets: baseUrl is required");
            return Promise.reject(new Error("Base URL is required"));
        }

        return RcAPI.apiRequest(`${apiBaseUrl}/assets?location=true&beaconInfo=true&sensorInfo=true`, "GET")
        .then(
            (response) => {
                return Promise.resolve(response.results);
            },
            (error) => {return Promise.reject(error);}
        );
    };

    static getAsset(assetUuid) {

        if (!assetUuid) {
            console.error("Data assetMonitoring getAsset: assetUuid is required");
            return;
        }

        const apiBaseUrl = getApiBaseUrl('/user-v1');

        if (!apiBaseUrl) {
            console.error("Data assetMonitoring getAccountBeacons: baseUrl is required");
            return;
        }

        return RcAPI.apiRequest(`${apiBaseUrl}/assets/${assetUuid}`, "GET")
        .then(
            (response) => {
                return Promise.resolve(response.results);
            },
            (error) => {return Promise.reject(error);}
        );
    };

    static postAsset(asset) { // POST (add) new asset

        if (!asset) {
            console.error("Data assetMonitoring addAsset: asset is required");
            return Promise.reject(new Error("Asset is required"));
        }

        const remoteAssetUpdate = convertToRemoteAssetUpdate(asset);

        const apiBaseUrl = getApiBaseUrl('/user-v1');

        if (!apiBaseUrl) {
            console.error("Data assetMonitoring addAsset: baseUrl is required");
            return Promise.reject(new Error("Base URL is required"));
        }

        return RcAPI.apiRequest(`${apiBaseUrl}/assets`, "POST", remoteAssetUpdate)
        .then(
            (response) => { // When creating a new asset, only the UUID is returned in the location header
                const {location} = response;
                if(location) {
                    const assetUuid = location.split('/').pop();
                    return Promise.resolve(assetUuid);
                }
                return Promise.reject(new Error("Asset UUID not found in response"));
            },
            (error) => {return Promise.reject(error);}
        )
    };

    static putAsset(asset) { // PUT (update) existing asset

        if (!asset) {
            console.error("Data assetMonitoring putAsset: asset is required");
            return Promise.reject(new Error("Asset is required"));
        }

        const apiBaseUrl = getApiBaseUrl('/user-v1');

        if (!apiBaseUrl) {
            console.error("Data assetMonitoring putAsset: baseUrl is required");
            return Promise.reject(new Error("Base URL is required"));
        }

        const remoteAssetUpdate = convertToRemoteAssetUpdate(asset);

        return RcAPI.apiRequest(`${apiBaseUrl}/assets/${asset.assetUuid}`, "PUT", remoteAssetUpdate)
        .catch(
            (error) => {return Promise.reject(error);}
        );
    }

    static deleteAsset(assetUuid) {
        
        if (!assetUuid) {
            console.error("Data assetMonitoring deleteAsset: assetUuid is required");
            return Promise.reject(new Error("Asset UUID is required"));
        }

        const apiBaseUrl = getApiBaseUrl('/user-v1');

        if (!apiBaseUrl) {
            console.error("Data assetMonitoring deleteAsset: baseUrl is required");
            return Promise.reject(new Error("Base URL is required"));
        }

        return RcAPI.apiRequest(`${apiBaseUrl}/assets/${assetUuid}`, "DELETE")
        .catch(
            (error) => {return Promise.reject(error);}
        );
    };

    static putAssetBeacon(assetUuid, beaconUuid) {

        if (!assetUuid || !beaconUuid) {
            console.error("Data assetMonitoring putAssetBeacon: assetUuid and beaconUuid are required");
            return Promise.reject(new Error("Both Asset UUID and Beacon UUID are required"));
        }

        const apiBaseUrl = getApiBaseUrl('/user-v1');

        if (!apiBaseUrl) {
            console.error("Data assetMonitoring putAssetBeacon: baseUrl is required");
            return Promise.reject(new Error("Base URL is required"));
        }

        return RcAPI.apiRequest(`${apiBaseUrl}/assets/${assetUuid}/beacon/${beaconUuid}`, "PUT")
        .catch(
            (error) => {return Promise.reject(error);}
        );
    };

    static deleteAssetBeacon(assetUuid, beaconUuid) {

        if (!assetUuid || !beaconUuid) {
            console.error("Data assetMonitoring deleteAssetBeacon: assetUuid and beaconUuid are required");
            return Promise.reject(new Error("Both Asset UUID and Beacon UUID are required"));
        }

        const apiBaseUrl = getApiBaseUrl('/user-v1');

        if (!apiBaseUrl) {
            console.error("Data assetMonitoring deleteAssetBeacon: baseUrl is required");
            return Promise.reject(new Error("Base URL is required"));
        }

        return RcAPI.apiRequest(`${apiBaseUrl}/assets/${assetUuid}/beacon`, "DELETE")
        .catch(
            (error) => {
                return Promise.reject(error);
            }
        );
    };

    static getAccountBeacons() {

        const apiBaseUrl = getApiBaseUrl('/user-v1');

        if (!apiBaseUrl) {
            console.error("Data assetMonitoring getAccountBeacons: baseUrl is required");
            return Promise.reject(new Error("Base URL is required"));
        }

        return RcAPI.apiRequest(`${apiBaseUrl}/beacons?location=true&assetInfo=true&sensorInfo=true`, "GET")
        .then(
            (response) => {
                return Promise.resolve(response.results);
            },
            (error) => {return Promise.reject(error);}
        );
    };

    static getBeacon(beaconUuid) {
            
        if (!beaconUuid) {
            console.error("Data assetMonitoring getBeacon: beaconUuid is required");
            return Promise.reject(new Error("Beacon UUID is required"));
        }

        const apiBaseUrl = getApiBaseUrl('/user-v1');

        if (!apiBaseUrl) {
            console.error("Data assetMonitoring getBeacon: baseUrl is required");
            return Promise.reject(new Error("Base URL is required"));
        }

        return RcAPI.apiRequest(`${apiBaseUrl}/beacons/${beaconUuid}`, "GET")
        .then(
            (response) => {
                return Promise.resolve(response.results);
            },
            (error) => {return Promise.reject(error);}
        );
    };

    static putBeacon(beacon) {
            
        if (!beacon) {
            console.error("Data assetMonitoring putBeacon: beacon is required");
            return Promise.reject(new Error("Beacon is required"));
        }

        const apiBaseUrl = getApiBaseUrl('/user-v1');

        if (!apiBaseUrl) {
            console.error("Data assetMonitoring putBeacon: baseUrl is required");
            return Promise.reject(new Error("Base URL is required"));
        }

        const remoteBeaconUpdate = convertToRemoteBeaconUpdate(beacon);

        return RcAPI.apiRequest(`${apiBaseUrl}/beacons/${beacon.beaconUuid}/visibility`, "PUT", remoteBeaconUpdate)
        .then(
            (response) => {
                return Promise.resolve(response);
            },
            (error) => {return Promise.reject(error);}
        );
    };
}

export const DispatchHelpers = {

    loadAssets: () => (dispatch, getState) => { // redux-thunk function pattern (dispatch and getState are provided by redux-thunk)
        dispatch(ACTION_CREATORS.updatingAssets()); // sets a pending flag, resets error message

        return AssetMonitoringApi.getAccountAssets()
        .then(
            (assets) => {
                dispatch(ACTION_CREATORS.setAssets(assets));
                return Promise.resolve();
            },
            (error) => {
                const errorMessage = error.message ? error.message : "An error occurred";
                dispatch(ACTION_CREATORS.setAssets(undefined, errorMessage));
                return Promise.reject(error);
            }
        );
    },

    setAssetVisible: (assetUuid, visible) => (dispatch, getState) => {
            
        const asset = getState().data.assetMonitoring.assets.find(asset => asset.assetUuid === assetUuid);

        if (!asset) {
            console.error("Data assetMonitoring setAssetVisible: asset not found");
            return;
        }

        dispatch(ACTION_CREATORS.setAsset({
            ...asset,
            visible: visible
        }));
    },

    setAssetName: (assetUuid, name) => (dispatch, getState) => {
        const assetFromState = getState().data.assetMonitoring.assets.find(asset => asset.assetUuid === assetUuid);
        
        if (!assetFromState) {
            console.error("Data assetMonitoring setAssetName: asset not found");
            return Promise.reject(new Error("Asset not found"));
        }

        dispatch(ACTION_CREATORS.updatingAssetName(assetFromState)); // sets a pending flag, resets error message

        const updatedAsset = { ...assetFromState, name: name };

        // if the asset is new, don't save it to the cloud yet
        if(updatedAsset.assetUuid === NEW_ASSET_TEMPORARY_UUID) {
            dispatch(ACTION_CREATORS.setAsset(updatedAsset));
            return Promise.resolve();
        }

        return AssetMonitoringApi.putAsset(updatedAsset)
            .then(
                (putAssetResponse) => {
                    dispatch(ACTION_CREATORS.setAsset(updatedAsset));
                    return putAssetResponse;
                },
                (error) => { 
                    const errorMessage = error.message ? error.message : "An error occurred";
                    dispatch(ACTION_CREATORS.setAsset(assetFromState, errorMessage));
                    return Promise.reject(error);
                }
            );
    },

    setAllAssetsVisible: (visible) => (dispatch, getState) => { // redux-thunk
            
        dispatch(ACTION_CREATORS.setAssets(
            getState().data.assetMonitoring.assets.map(asset => {
                return {
                    ...asset,
                    visible: visible
                }
            }), undefined, true
        ));
    },

    addNewAsset: () => (dispatch, getState) => { // redux-thunk

        dispatch(ACTION_CREATORS.setAsset({
            ...NEW_ASSET_TEMPLATE,
            editing: true
        })); // add the new asset to the local state (it will have a temporary UUID until it is saved to the cloud);
    },

    cancelAddNewAsset: () => (dispatch, getState) => {

        dispatch(ACTION_CREATORS.removeAsset(NEW_ASSET_TEMPORARY_UUID)); // remove the new asset from the local state
    },

    startAssetEdit: (assetUuid) => (dispatch, getState) => {
            
        const asset = getState().data.assetMonitoring.assets.find(asset => asset.assetUuid === assetUuid);

        if (!asset) {
            console.error("Data assetMonitoring startAssetEdit: asset not found");
            return;
        }

        dispatch(ACTION_CREATORS.setAsset({
            ...asset,
            editing: true,
            originalAsset: { ...asset } // store a copy of the original asset data for cancelling edits
        }));
    },

    saveAssetEdit: (asset) => (dispatch, getState) => {

        const assetFromState = getState().data.assetMonitoring.assets.find(assetFromState => assetFromState.assetUuid === asset.assetUuid);

        if (!assetFromState) {
            console.error("Data assetMonitoring saveAssetEdit: asset not found in state");
            return Promise.reject(new Error("Asset not found"));
        }

        let alsoUpdateBeacon = false;
        
        if (assetFromState.beaconUuid !== asset.beaconUuid) {
            alsoUpdateBeacon = true; // either the new asset also includes a beacon assignment (undefined -> string) or the beacon assignment has changed for a pre-existing asset (string -> new string)
        }

        dispatch(ACTION_CREATORS.updatingAsset(asset)); // sets a pending flag, resets error message

        if (asset.assetUuid === NEW_ASSET_TEMPORARY_UUID) {
            return AssetMonitoringApi.postAsset(asset)
            .then(
                (addedAssetUuid) => { // When adding new asset, the UUID is returned from the cloud (in location header)
                    asset.assetUuid = addedAssetUuid;

                    const replaceNewLocalAsset = true;

                    if (!asset.beaconUuid) { // if (!alsoUpdateBeacon) {
                        asset.editing = false;
                        dispatch(ACTION_CREATORS.setAsset(asset, undefined, replaceNewLocalAsset)); // add the real asset to state

                        return Promise.resolve(asset.assetUuid);
                    }

                    // update the asset with the new beacon assignment and vice versa
                    const beacon = getState().data.assetMonitoring.beacons.find(beacon => beacon.beaconUuid === asset.beaconUuid);

                    if (!beacon) {
                        return Promise.reject(new Error("assigning beacon to new asset error: Beacon not found in state"));
                    }

                    return AssetMonitoringApi.putAssetBeacon(asset.assetUuid, asset.beaconUuid)
                    .then(
                        () => {
                            asset.editing = false;
                            dispatch(ACTION_CREATORS.setAsset(asset, undefined, replaceNewLocalAsset)); // add the real asset to state
                            
                            const updatedBeacon = { ...beacon, assetUuid: asset.assetUuid };
                            
                            dispatch(ACTION_CREATORS.setBeacon(updatedBeacon));
                            
                            return Promise.resolve(asset.assetUuid);
                        },
                        (error) => {
                            asset.editing = true;
                            const errorMessage = error.message ? error.message : "An error occurred";
                            dispatch(ACTION_CREATORS.setAsset(asset, errorMessage));
                            return Promise.reject(error);
                        }
                    );
                },
                (error) => {
                    asset.editing = true;
                    const errorMessage = error.message ? error.message : "An error occurred";
                    dispatch(ACTION_CREATORS.setAsset(asset, errorMessage));
                    return Promise.reject(error);
                }
            );
        }

        // else update an existing asset
        return Promise.all([
            new Promise((resolve, reject) => {
                AssetMonitoringApi.putAsset(asset)
                .then(
                    (putAssetResponse) => { resolve(putAssetResponse) },
                    (error) => { reject(error) }
                );
            }),
            new Promise((resolve, reject) => {
                if (alsoUpdateBeacon) {
                    if (!asset.beaconUuid && asset.beacon !== "") {
                        AssetMonitoringApi.deleteAssetBeacon(asset.assetUuid, assetFromState.beaconUuid)
                        .then(
                            () => { 
                                // now that the beacon assignment is being removed, clear all the beacon data from the asset and vice versa
                                const beaconFromState = getState().data.assetMonitoring.beacons.find(beacon => beacon.beaconUuid === assetFromState.beaconUuid);
                                if(beaconFromState){
                                    const previousBeacon = { ...beaconFromState };
                                    clearAssetBeaconAssignment(asset, previousBeacon);
                                    resolve({previousBeacon});
                                }
                                else{
                                    reject(new Error("Unassigning beacon from asset error: Beacon not found in state"));
                                }
                            },
                            (error) => { reject(error) }
                        );
                    } else {
                        AssetMonitoringApi.putAssetBeacon(asset.assetUuid, asset.beaconUuid)
                        .then(
                            () => {
                                let previousBeacon = undefined;
                                let newBeacon = undefined;

                                // Also if the asset was previously assigned to a beacon, clear that beacon's assignment
                                if (assetFromState.beaconUuid) {
                                    const previousBeaconFromState = getState().data.assetMonitoring.beacons.find(beacon => beacon.beaconUuid === assetFromState.beaconUuid);
                                    if (previousBeaconFromState) {
                                        previousBeacon = { ...previousBeaconFromState };
                                        clearAssetBeaconAssignment(undefined, previousBeacon);
                                    }
                                }

                                // update the asset with the new beacon assignment and vice versa
                                const newBeaconFromState = getState().data.assetMonitoring.beacons.find(beacon => beacon.beaconUuid === asset.beaconUuid);
                                if (newBeaconFromState) {
                                    newBeacon = { ...newBeaconFromState };
                                    setAssetBeaconAssignment(asset, newBeacon);

                                    resolve({previousBeacon, newBeacon});
                                }
                                else{
                                    reject(new Error("Assigning beacon to asset error: Beacon not found in state"));
                                }
                            },
                            (error) => { reject(error) }
                        );
                    }
                } else {
                    resolve({});
                }
            })
        ]).then(
            ([_, beacons = {}]) => {
                
                const { newBeacon, previousBeacon } = beacons;

                const updatedAsset = {
                    ...asset,
                    editing: false,
                    originalAsset: undefined // clear the original asset data used for cancelling edits
                };

                dispatch(ACTION_CREATORS.setAsset(updatedAsset));

                if(alsoUpdateBeacon && newBeacon) {
                    dispatch(ACTION_CREATORS.setBeacon(newBeacon));
                }

                if(alsoUpdateBeacon && previousBeacon) {
                    dispatch(ACTION_CREATORS.setBeacon(previousBeacon));
                }

                return Promise.resolve(updatedAsset.assetUuid);
            },
            (firstError) => {
                asset.editing = true;
                const errorMessage = firstError.message ? firstError.message : "An error occurred";
                dispatch(ACTION_CREATORS.setAsset(asset, errorMessage));
                return Promise.reject(firstError);
            }
        );
    },

    cancelAssetEdit: () => (dispatch, getState) => {

        // Check if there is an existing asset or a new asset in editing mode
        let asset = getState().data.assetMonitoring.assets.find(asset => asset.editing === true);
        
        // No editing or new asset found
        if (!asset) {
            console.error("Data assetMonitoring cancelAssetEdit: asset not found");
            return Promise.resolve();
        }

        if (asset.assetUuid === NEW_ASSET_TEMPORARY_UUID) {
            dispatch(ACTION_CREATORS.removeAsset(NEW_ASSET_TEMPORARY_UUID)); // remove the new asset from the local state
        } else {
            // Existing asset with a stored original snapshot
            if (asset.originalAsset) {
                dispatch(ACTION_CREATORS.setAsset({
                    ...asset.originalAsset,
                    editing: false,
                    originalAsset: undefined,
                }));
                return Promise.resolve();
            }

            // Fallback if for some reason there's no snapshot
            dispatch(ACTION_CREATORS.setAsset({
                ...asset,
                editing: false,
            }));
        }

        return Promise.resolve();
    },

    deleteAsset: (assetUuid) => (dispatch, getState) => {
        
        if (!assetUuid) {
            console.error("Data assetMonitoring deleteAsset: assetUuid is required");
            return Promise.reject(new Error("assetUuid is required"));
        }

        const asset = getState().data.assetMonitoring.assets.find(asset => asset.assetUuid === assetUuid);

        if (!asset) {
            console.error("Data assetMonitoring deleteAsset: asset not found in state");
            return Promise.reject(new Error("Asset not found"));
        }

        dispatch(ACTION_CREATORS.updatingAssets()); // sets a pending flag, resets error message
        dispatch(ACTION_CREATORS.updatingAsset(asset)); // sets a pending flag, resets error message

        return AssetMonitoringApi.deleteAsset(assetUuid)
        .then(
            () => {
                dispatch(ACTION_CREATORS.setAssets());

                // if the asset was assigned to a beacon, clear the beacon assignment
                if (asset.beaconUuid) {
                    const beacon = getState().data.assetMonitoring.beacons.find(beacon => beacon.beaconUuid === asset.beaconUuid);
                    if (beacon) {
                        const updatedBeacon = { ...beacon };
                        clearAssetBeaconAssignment(undefined, updatedBeacon);
                        dispatch(ACTION_CREATORS.setBeacon(updatedBeacon));
                    }                    
                }

                // remove the asset from the local state
                dispatch(ACTION_CREATORS.removeAsset(assetUuid));
                return Promise.resolve(assetUuid);
            },
            (error) => {
                const errorMessage = error.message ? error.message : "An error occurred";
                dispatch(ACTION_CREATORS.setAssets());
                dispatch(ACTION_CREATORS.setAsset(asset, errorMessage));
                return Promise.reject(error);
            }
        );
    },

    loadBeacons: () => (dispatch, getState) => {
            
        dispatch(ACTION_CREATORS.updatingBeacons()); // sets a pending flag, resets error message

        return AssetMonitoringApi.getAccountBeacons()
        .then(
            (beacons) => {
                dispatch(ACTION_CREATORS.setBeacons(beacons));
                return Promise.resolve();
            },
            (error) => {
                const errorMessage = error.message ? error.message : "An error occurred";
                dispatch(ACTION_CREATORS.setBeacons(undefined, errorMessage));
                return Promise.reject(error);
            }
        );
    },

    toggleBeaconVisibility: (beaconUuid) => (dispatch, getState) => {
        const beaconFromState = getState().data.assetMonitoring.beacons.find(beacon => beacon.beaconUuid === beaconUuid);
        
        if (!beaconFromState) {
            console.error("Data assetMonitoring hideBeacon: beacon not found");
            return Promise.reject(new Error("Beacon not found"));
        }

        dispatch(ACTION_CREATORS.updatingBeacons()); // sets a pending flag, resets error message
        dispatch(ACTION_CREATORS.updatingBeacon(beaconFromState)); // sets a pending flag, resets error message

        const updatedBeacon = { ...beaconFromState, visibility: !beaconFromState.visibility };
        
        return AssetMonitoringApi.putBeacon(updatedBeacon)
            .then(
                (putBeaconResponse) => {
                    dispatch(ACTION_CREATORS.setBeacons());
                    dispatch(ACTION_CREATORS.setBeacon(updatedBeacon));
                    return Promise.resolve(putBeaconResponse);
                },
                (error) => { 
                    const errorMessage = error.message ? error.message : "An error occurred";
                    dispatch(ACTION_CREATORS.setBeacons());
                    dispatch(ACTION_CREATORS.setBeacon(beaconFromState, errorMessage));
                    return Promise.reject(error);
                }
            );
    },
}

export const Selectors = {

    loaded: (state) => {
        return state.data.assetMonitoring.assetsLoaded;
    },

    loading: (state) => { // used by ResourceGroups' Assets' loading prop
        return state.data.assetMonitoring.assetsPending;
    },

    assetEditModeActive: (state) => {
        return !!state.data.assetMonitoring.assets.find(asset => asset.editing === true);
    },

    activeNewAsset: (state) => {
        return state.data.assetMonitoring.assets.find(asset => asset.assetUuid === NEW_ASSET_TEMPORARY_UUID);
    },

    assetNameChangePending: (state, assetUuid) => {
        const asset = state.data.assetMonitoring.assets.find(asset => asset.assetUuid === assetUuid);
        return asset ? asset.nameChangePending : false;
    },

    assetLoadOrSavePending: (state, assetUuid) => {
        const asset = state.data.assetMonitoring.assets.find(asset => asset.assetUuid === assetUuid);

        if (asset) return asset.pending;

        return false;
    },

    beaconAsset: (state, beaconId) => {
        const beacon = state.data.assetMonitoring.beacons.find(beacon => beacon.beaconUuid === beaconId);
        if (!beacon) return undefined;
        return state.data.assetMonitoring.assets.find(asset => asset.assetUuid === beacon.assetUuid);
    },

    assetUuids: (state) => {
        return state.data.assetMonitoring.assets.map(asset => asset.assetUuid);
    },

    asset: (state, assetUuid) => {
        return state.data.assetMonitoring.assets.find(asset => asset.assetUuid === assetUuid);
    },

    nextAssetToSelect: (state, assetUuid) => {

        if(state.data.assetMonitoring.assets.length === 1) return undefined; // if there is only one asset, there is no next/prev asset

        let index = state.data.assetMonitoring.assets.findIndex(asset => asset.assetUuid === assetUuid);
        
        if (index === -1) index = 0;

        const nextAsset = state.data.assetMonitoring.assets[(index + 1) % state.data.assetMonitoring.assets.length];

        if(nextAsset) return nextAsset;

        const prevAsset = state.data.assetMonitoring.assets[(index - 1) % state.data.assetMonitoring.assets.length];
        
        if(prevAsset) return prevAsset;

        return undefined;
    },

    assetName: (state, assetUuid) => {
        const asset = state.data.assetMonitoring.assets.find(asset => asset.assetUuid === assetUuid);
        return asset ? asset.name : undefined;
    },

    isAssetVisible: (state, assetUuid) => {
        return state.data.assetMonitoring.assets.find(asset => asset.assetUuid === assetUuid).visible;
    },
    
    beaconsLoaded: (state) => {
        return state.data.assetMonitoring.beaconsLoaded;
    },

    beaconsPending: (state) => {
        return state.data.assetMonitoring.beaconsPending;
    },

    beaconsError: (state) => {
        return state.data.assetMonitoring.beaconsErrorMessage;
    },

    beacon: (state, beaconId) => {
        return state.data.assetMonitoring.beacons.find(beacon => beacon.beaconUuid === beaconId);
    },

    unassignedBeacons: (state) => {
        return state.data.assetMonitoring.beacons.filter(beacon => !beacon.assetUuid);
    },

    beacons: (state) => state.data.assetMonitoring.beacons,

    availableBeacons: (state, assetUuid) => {
        const notAssignedVisibleBeacons = state.data.assetMonitoring.beacons.filter(beacon => !beacon.assetUuid && beacon.visibility !== false);
        const assignedBeaconsToCurrentAsset = assetUuid !== NEW_ASSET_TEMPORARY_UUID ? state.data.assetMonitoring.beacons.filter(beacon => beacon.assetUuid === assetUuid) : []; // should be one but to avoid checking for undefined or null I'm using filter
        
        return notAssignedVisibleBeacons.concat(assignedBeaconsToCurrentAsset);
    },

    beaconsSavePending: (state) => {
        return state.data.assetMonitoring.beacons.some(beacon => beacon.pending);
    },
};
